import { CSSProperties, Dispatch, SetStateAction, useState } from 'react';

import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import produce from 'immer';
import styled from 'styled-components';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { IconButton } from '@components/icon-button';
import { IconDrag } from '@components/icons/drag';
import { IconEdit } from '@components/icons/edit';
import { IconRemove } from '@components/icons/remove';
import { IconReturn } from '@components/icons/return';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { Option } from './lists/sidebar';

type DraggableOptionProps = {
  option: Option;
  index: number;
  removeOption: (isCancel?: boolean) => void;
  editOption: (index: number, option: Option) => void;
  newOptionValue: string | null;
  isDragging?: boolean;
  displayMissing?: boolean;
  resetCustomError?: Dispatch<SetStateAction<boolean>>;
  setOptions?: Dispatch<SetStateAction<Option[]>>;
  isDuplicateValue?: (value: string, shortId: string) => boolean;
};

const Form = createForm<Option>();

export const DraggableOption = (props: DraggableOptionProps) => {
  const {
    option,
    index,
    removeOption,
    editOption,
    newOptionValue,
    isDragging = false,
    displayMissing,
    resetCustomError,
    isDuplicateValue,
    setOptions,
  } = props;
  const { t } = useScopedTranslate(OptionLocale);
  const [isNew, setIsNew] = useState<boolean>(newOptionValue === option.value);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [onChangeOption, setOnChangeOption] = useState(option);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: option.value,
    data: {
      order: index,
    },
  });

  const formStore = useCreateFormStore();

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    cursor: 'grab',
    ...(isDragging && { opacity: 0.5 }),
  };

  const formErrors: { [key: string]: string } = {};
  const formValues = formStore.useFormValues();
  const values = formValues as Option;
  const checkValues = () => {
    if (values.label === '' && !formErrors['label']) {
      formErrors['label'] = t('labelError');
    }
    if (values.value === '' && !formErrors['value']) {
      formErrors['value'] = t('valueError');
    }
    if (isDuplicateValue?.(values.value, option.shortId) && !formErrors['value']) {
      formErrors['value'] = t('uniqueError');
    }
    formStore.actions.setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const save = () => {
    resetCustomError?.(false);
    if (!checkValues()) return;

    const obj: Option = { ...values };
    if (option.shortId) {
      obj.shortId = option.shortId;
    }

    editOption(index, obj);
    if (isNew) {
      setIsNew(false);
    } else {
      setShowEdit(false);
    }
  };

  const onCancel = () => {
    resetCustomError?.(false);
    if (isNew) {
      removeOption(true);
    } else {
      setOnChangeOption(option);
      setShowEdit(false);
    }
  };

  const updateOptions = (type: 'value' | 'label', index: number, value: string) => {
    setOptions?.((prevOptions) =>
      prevOptions.map((option, idx) => (idx === index ? { ...option, [type]: value } : option))
    );
  };

  return showEdit || isNew ? (
    <StyledCard variant="gray" space={2}>
      <Form store={formStore} onSubmit={save}>
        <Stack space={2} orientation={'vertical'}>
          <Stack style={{ width: '100%' }} space={2}>
            <Form.TextField
              id="label"
              autoFocus
              placeholder={t('addLabel')}
              label={t('add')}
              hideLabel
              defaultValue={onChangeOption ? onChangeOption.label : ''}
              onChange={(value) => {
                setOnChangeOption(
                  produce((draft) => {
                    draft.label = value;
                  })
                );
                !showEdit && updateOptions('label', index, value);
              }}
              maxChars={255}
            />
            <Form.TextField
              id="value"
              placeholder={t('addValue')}
              label={t('add')}
              hideLabel
              defaultValue={onChangeOption ? onChangeOption.value : ''}
              onChange={(value) => {
                setOnChangeOption(
                  produce((draft) => {
                    draft.value = value;
                  })
                );
                !showEdit && updateOptions('value', index, value);
              }}
              maxChars={255}
            />
          </Stack>
          <Form.Actions>
            <ButtonGroup align="right">
              <Button onClick={onCancel} text={t('core.cancel')} variant="secondary" />
              <Form.Submit
                variant="primary-blue"
                text={isNew ? t('add') : t('update')}
                iconLeft={<IconReturn />}
              />
            </ButtonGroup>
          </Form.Actions>
        </Stack>
      </Form>
    </StyledCard>
  ) : (
    <div ref={setNodeRef} style={style}>
      <Card
        key={`option-${index}`}
        variant="transparent"
        interactive
        space={1}
        {...attributes}
        {...listeners}
      >
        <Stack space={4} orientation="horizontal" align="center" justify="space-between">
          <Stack orientation="horizontal" align="center" space={1}>
            <StyledDragIcon align="center" justify="center">
              <IconDrag color="elementPurple50" />
            </StyledDragIcon>
            <Stack space={1}>
              <Stack orientation="horizontal" space={1} align="center">
                {Boolean(option.label) ? (
                  <Text text={option.label} font="regular14" style={{ wordBreak: 'break-word' }} />
                ) : (
                  <Text text={t('noLabel')} font="regular14" color="textHelper" />
                )}
              </Stack>
              {displayMissing && <FormError text={t('optionMissing')} />}
              <Stack orientation="horizontal" space={1} align="center">
                {Boolean(option.value) ? (
                  <Text text={option.value} font="regular14" style={{ wordBreak: 'break-word' }} />
                ) : (
                  <Text text={t('noLabel')} font="regular14" color="textHelper" />
                )}
              </Stack>
              {displayMissing && <FormError text={t('optionMissing')} />}
            </Stack>
          </Stack>
          <Stack orientation="horizontal">
            <IconButton
              icon={<IconEdit />}
              dataTestid="edit-option"
              onClick={() => setShowEdit(true)}
              variant="ghost-purple"
            />
            <IconButton
              icon={<IconRemove />}
              dataTestid="remove-option"
              onClick={() => removeOption(false)}
              variant="ghost-purple"
            />
          </Stack>
        </Stack>
      </Card>
    </div>
  );
};

const StyledCard = styled(Card)({
  border: `${pxToRem(2)} solid`,
  borderColor: `${theme.color.elementBlue100} !important`,
  background: `${theme.color.uiTrans5} !important`,
});

const StyledDragIcon = styled(Stack)({
  width: pxToRem(40),
  minWidth: pxToRem(40),
});

type QuestionCardProps = {
  option: { label: string; value: string };
  index: number;
};

export const OptionCard = (props: QuestionCardProps) => {
  const { option, index } = props;
  const { t } = useScopedTranslate(OptionLocale);
  const { setNodeRef } = useDroppable({
    id: option.value,
    data: {
      order: index,
    },
  });

  return (
    <div ref={setNodeRef}>
      <Card key={`option-${index}`} space={2}>
        <Stack space={4} orientation="horizontal" align="center" justify="space-between">
          <Stack orientation="horizontal" align="center" space={1}>
            <StyledDragIcon align="center" justify="center">
              <IconDrag color="elementPurple50" />
            </StyledDragIcon>
            <Stack space={1}>
              <Stack orientation="horizontal" space={1} align="center">
                {Boolean(option.label) ? (
                  <Text text={option.label} font="regular14" style={{ wordBreak: 'break-word' }} />
                ) : (
                  <Text text={t('noTranslation')} font="regular14" color="textHelper" />
                )}
              </Stack>
              <Stack orientation="horizontal" space={1} align="center">
                {Boolean(option.value) ? (
                  <Text text={option.value} font="regular14" style={{ wordBreak: 'break-word' }} />
                ) : (
                  <Text text={t('noTranslation')} font="regular14" color="textHelper" />
                )}
              </Stack>
            </Stack>
          </Stack>
          <Stack orientation="horizontal">
            <IconButton icon={<IconEdit />} variant="ghost-purple" />
            <IconButton icon={<IconRemove />} variant="ghost-purple" />
          </Stack>
        </Stack>
      </Card>
    </div>
  );
};

const OptionLocale = {
  en: {
    addOption: 'Add option',
    add: 'Add',
    update: 'Update',
    langError: 'Translation missing',
    optionMissing: 'Option missing',
    noTranslation: 'No translation',
    addLabel: 'Add label',
    noLabel: 'No label',
    addValue: 'Add value',
    labelError: 'Label missing',
    valueError: 'Value missing',
    uniqueError: 'Duplicate value',
  },
};
